import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { StyleClassModule } from 'primeng/styleclass';
import { Observable, Subscription, interval, startWith, switchMap } from 'rxjs';

import { AppConfigurator } from './app.configurator';
import { LayoutService } from '../service/layout.service';
import { TicketNotificationItem, TicketNotificationsPayload, TicketNotificationsService } from '@/services/ticket-notifications.service';
import { ButtonModule } from 'primeng/button';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { JalaliDatePipe } from '@/pipes/jalali-date.pipe';

// ✅ جایگزین JWT
import { AuthService, CurrentUser } from '../../auth/auth.service';

@Component({
  selector: 'app-topbar',
  standalone: true,
  imports: [RouterModule, CommonModule, StyleClassModule, ButtonModule, OverlayBadgeModule, AppConfigurator, JalaliDatePipe],
  template: `
    <div class="layout-topbar">
      <div class="layout-topbar-logo-container">
        <button class="layout-menu-button layout-topbar-action" (click)="layoutService.onMenuToggle()">
          <i class="pi pi-bars"></i>
        </button>

        <a class="layout-topbar-logo" routerLink="/">
          <img
            src="assets/images/12.png"
            alt="Sarir Logistic"
            class="block mx-auto"
            style="max-width: 160px !important; height: auto;"
          />
        </a>
      </div>

      <div class="layout-topbar-actions">
        @if (user$ | async; as user) {
          <div
            class="hidden md:flex align-items-center px-3"
            style="cursor: default; height: 3rem; line-height: 3rem; align-items: center; direction: rtl;"
          >
            <span class="font-medium" style="white-space: nowrap; line-height: 1;">
              <span dir="ltr">
                {{
                  (user.fullName ||
                    (((user.firstName || '') + ' ' + (user.lastName || '')).trim()) ||
                    user.username) + ''
                }}
              </span>
              ، خوش آمدی!
            </span>
          </div>
        }

        <div
          class="layout-topbar-notifications"
          *ngIf="notifications$ | async as payload"
          #notificationsContainer
        >
          <button
            type="button"
            class="layout-topbar-action layout-topbar-action-bell"
            (click)="toggleNotifications($event)"
            [title]="'تیکت‌های منتظر پاسخ را ببینید'"
            [class.has-count]="(payload?.count ?? 0) > 0"
            [class.shake]="(payload?.count ?? 0) > 0"
          >
            <ng-container *ngIf="(payload?.count ?? 0) > 0; else iconOnly">
              <p-overlaybadge value="{{ payload.count }}" severity="danger">
                <i class="pi pi-bell"></i>
              </p-overlaybadge>
            </ng-container>
            <ng-template #iconOnly>
              <i class="pi pi-bell"></i>
            </ng-template>
          </button>

          <div class="notifications-dropdown" *ngIf="notificationsOpen && payload">
            <ng-container *ngIf="payload.count > 0; else noNotifications">
              <div class="notifications-list">
                <div class="notification-item" *ngFor="let ticket of payload.items" (click)="viewTicket(ticket)">
                  <div class="notification-code">{{ ticket.code || '—' }}</div>
                  <div class="notification-subject">{{ ticket.subject || 'بدون موضوع' }}</div>
                  <div class="notification-updated">{{ ticket.updatedAt | jalaliDate:'YYYY/MM/DD HH:mm' }}</div>
                </div>
                <button pButton type="button" label="مشاهده همه" class="w-full" (click)="goToMyTickets(payload)"></button>
              </div>
            </ng-container>
            <ng-template #noNotifications>
              <div class="notification-empty">فعلاً تیکتی منتظر پاسخی نیست.</div>
            </ng-template>
          </div>
        </div>

        <div class="layout-config-menu">
          <button type="button" class="layout-topbar-action" (click)="toggleDarkMode()">
            <i [ngClass]="{ 'pi ': true, 'pi-moon': layoutService.isDarkTheme(), 'pi-sun': !layoutService.isDarkTheme() }"></i>
          </button>

          <div class="relative">
            <button
              class="layout-topbar-action layout-topbar-action-highlight"
              pStyleClass="@next"
              enterFromClass="hidden"
              enterActiveClass="animate-scalein"
              leaveToClass="hidden"
              leaveActiveClass="animate-fadeout"
              [hideOnOutsideClick]="true"
            >
              <i class="pi pi-palette"></i>
            </button>
            <app-configurator />
          </div>
        </div>

        <button
          class="layout-topbar-menu-button layout-topbar-action"
          pStyleClass="@next"
          enterFromClass="hidden"
          enterActiveClass="animate-scalein"
          leaveToClass="hidden"
          leaveActiveClass="animate-fadeout"
          [hideOnOutsideClick]="true"
        >
          <i class="pi pi-ellipsis-v"></i>
        </button>

        <div class="layout-topbar-menu hidden lg:block">
          <div class="layout-topbar-menu-content">
            <button type="button" class="layout-topbar-action">
              <i class="pi pi-calendar"></i>
              <span>Calendar</span>
            </button>
            <button type="button" class="layout-topbar-action">
              <i class="pi pi-inbox"></i>
              <span>Messages</span>
            </button>
            <button type="button" (click)="logout()" class="layout-topbar-action">
              <i class="pi pi-fw pi-sign-out"></i>
              <span>Exit</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .layout-topbar-notifications {
        position: relative;
        margin-left: 0.5rem;
      }

      .layout-topbar-action-bell {
        position: relative;
      }

      .layout-topbar-action-bell .topbar-badge {
        display: none;
      }
      .layout-topbar-action-bell.shake {
        animation: bell-shake 1s infinite;
      }

      @keyframes bell-shake {
        0%,
        100% {
          transform: translateY(0);
        }
        20%,
        80% {
          transform: translateY(-1px);
        }
        40%,
        60% {
          transform: translateY(1px);
        }
      }

      .notifications-dropdown {
        position: absolute;
        top: 3.4rem;
        right: 0;
        z-index: 15;
        width: 320px;
        background: #fff;
        border-radius: 0.75rem;
        border: 1px solid #e5e7eb;
        padding: 0.75rem;
        box-shadow: 0 12px 30px rgba(15, 23, 42, 0.15);
      }

      .notifications-list {
        display: flex;
        flex-direction: column;
        gap: 0.85rem;
      }

      .notification-item {
        padding: 0.65rem 0.75rem;
        border: 1px solid #e5e7eb;
        border-radius: 0.65rem;
        cursor: pointer;
      }

      .notification-item:hover {
        background: #f8fafc;
      }

      .notification-code {
        font-weight: 700;
        font-size: 0.85rem;
      }

      .notification-subject {
        font-size: 0.95rem;
        color: #0f172a;
      }

      .notification-updated {
        font-size: 0.75rem;
        color: #475569;
      }

      .notification-empty {
        padding: 0.75rem;
        font-size: 0.9rem;
        color: #64748b;
      }
    `,
  ],
})
export class AppTopbar implements OnInit, OnDestroy {
  items!: MenuItem[];
  user$: Observable<CurrentUser | null>;
  notifications$!: Observable<TicketNotificationsPayload | null>;
  notificationsOpen = false;

  @ViewChild('notificationsContainer', { read: ElementRef }) notificationsContainer?: ElementRef<HTMLElement>;
  private refreshSub?: Subscription;

  constructor(
    public layoutService: LayoutService,
    private auth: AuthService,
    private router: Router,
    private notificationService: TicketNotificationsService,
  ) {
    this.user$ = this.auth.user$;
    this.notifications$ = this.notificationService.data$;
  }

  ngOnInit() {
  }

  ngOnDestroy() {
    this.refreshSub?.unsubscribe();
  }

  toggleDarkMode() {
    this.layoutService.layoutConfig.update((state) => ({ ...state, darkTheme: !state.darkTheme }));
  }

  @HostListener('document:click', ['$event'])
  closeNotifications(event: MouseEvent) {
    if (this.notificationsContainer?.nativeElement.contains(event.target as Node)) {
      return;
    }
    this.notificationsOpen = false;
  }

  logout() {
    // ✅ خروج با Keycloak
    this.auth.logout();
    // ❌ ریدایرکت به لاگین داخلی حذف شد
    // window.location.href = '/auth/login';
  }

  toggleNotifications(event: Event) {
    event.stopPropagation();
    this.notificationsOpen = !this.notificationsOpen;
  }

  viewTicket(ticket: TicketNotificationItem) {
    this.notificationService.acknowledgeTicket(ticket.id, ticket.updatedAt);
    this.notificationsOpen = false;
    this.router.navigate(['/ticketing', ticket.id]);
  }

  goToMyTickets(payload: TicketNotificationsPayload) {
    this.notificationsOpen = false;
    this.router.navigate(['/ticketing/my'], { queryParams: { waitingRole: payload.role } });
  }
}
